/**
 * Selector de empresa.
 *
 * Su primer trabajo no es cambiar de empresa: es **que se vea en cuál se
 * está**, siempre y sin desplegar nada.
 *
 * El segundo es el cambio, y aquí hay que ser honestos con lo que el
 * sistema hace de verdad: una sesión está atada a una empresa —va en el
 * token—, así que cambiar de sociedad **es volver a identificarse**. No se
 * disimula con una animación de «cambiando de empresa…»: se avisa antes,
 * porque quien esté a medio rellenar un formulario tiene que poder decir
 * que no.
 */

import { useEffect, useRef, useState } from 'react';
import type { Workspace } from '../session/workspace.js';

interface Props {
  workspace: Workspace;
  /** Cierra la sesión: el cambio de empresa pasa por el login. */
  onSwitch: (taxId: string) => void;
}

/** Dónde se deja el NIF para que el login lo traiga puesto. */
export const PENDING_COMPANY_KEY = 'fibwifire.login.taxId';

export function CompanySwitcher({ workspace, onSwitch }: Props): React.ReactNode {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const container = useRef<HTMLDivElement>(null);

  const name = workspace.company.tradeName ?? workspace.company.legalName;
  const canSwitch = workspace.otherCompanies.length > 0;

  // Cerrar al hacer clic fuera y con Escape: es un menú, no un modal.
  useEffect(() => {
    if (!open) return;

    const onPointer = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!canSwitch) {
    // Una sola sociedad: no se enseña nada.
    //
    // Antes se mostraba el nombre. Decir en qué empresa se está sólo informa
    // cuando hay más de una donde estar: con una sola, la respuesta es
    // siempre la misma y el hueco de la barra vale más que el dato. En
    // fibwiFIRE, además, el nombre comercial coincide con el del producto y
    // aparecía repetido junto al logo.
    //
    // Vuelve solo el día que alguien tenga acceso a una segunda sociedad,
    // que es justo cuando la pregunta pasa a tener respuesta.
    return null;
  }

  return (
    <div className="company" ref={container}>
      <button
        className="company__button"
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title={`${workspace.company.legalName} · ${workspace.company.taxId}`}
      >
        {name}
        <span className="company__caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="company__menu" role="listbox">
          <p className="company__current">
            <strong>{workspace.company.legalName}</strong>
            <span>{workspace.company.taxId} · actual</span>
          </p>

          {workspace.otherCompanies.map((other) => (
            <button
              key={other.id}
              className="company__option"
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => setConfirming(other.taxId)}
            >
              <strong>{other.tradeName ?? other.legalName}</strong>
              <span>{other.taxId}</span>
            </button>
          ))}

          {confirming !== null && (
            <div className="company__confirm">
              <p>
                Cambiar de empresa cierra la sesión y hay que volver a entrar.
                Lo que tenga sin guardar se perderá.
              </p>
              <div className="company__confirm-actions">
                <button
                  className="button button--primary"
                  type="button"
                  onClick={() => {
                    // El login recogerá el NIF para no tener que teclearlo.
                    localStorage.setItem(PENDING_COMPANY_KEY, confirming);
                    onSwitch(confirming);
                  }}
                >
                  Cerrar sesión y cambiar
                </button>
                <button
                  className="button button--quiet"
                  type="button"
                  onClick={() => setConfirming(null)}
                >
                  Seguir aquí
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
